import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { ProfileAliasDto } from '../../services/socialService';
import type { JointAccountAlias } from '../../services/jointAccountService';

export interface ProfileAliasNavigatorProps {
  mode: 'personal' | 'business';
  selectedAliasId: string | null;
  personalAliases?: ProfileAliasDto[];
  teamMembers?: JointAccountAlias[];
  businessAliases?: ProfileAliasDto[];
  onSelectPersonalAlias: (alias: ProfileAliasDto) => void;
  onSelectTeamMember: (member: JointAccountAlias) => void;
  onSelectBusinessAlias: (alias: ProfileAliasDto) => void;
  onManageAliases?: () => void;
  viewerHasBusinessFullAccess?: boolean;
  onSetPrimaryBusinessAlias?: (alias: ProfileAliasDto) => void;
}

const selectedPaperSx = {
  border: 2,
  borderColor: 'primary.main',
  bgcolor: 'action.selected',
};

const ProfileAliasNavigator: React.FC<ProfileAliasNavigatorProps> = ({
  mode,
  selectedAliasId,
  personalAliases = [],
  teamMembers = [],
  businessAliases = [],
  onSelectPersonalAlias,
  onSelectTeamMember,
  onSelectBusinessAlias,
  onManageAliases,
  viewerHasBusinessFullAccess = false,
  onSetPrimaryBusinessAlias,
}) => {
  if (mode === 'personal') {
    const activeAliases = personalAliases.filter(
      (a) => a.bitIsActive && !(a.chrAliasName ?? '').includes('*Deleted*')
    );
    if (activeAliases.length === 0) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Your aliases
          </Typography>
          {onManageAliases && (
            <Link component="button" variant="body2" onClick={onManageAliases} sx={{ fontWeight: 500 }}>
              Create and Manage Aliases
            </Link>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {activeAliases.map((alias) => {
            const isSelected = alias.idAliasID === selectedAliasId;
            return (
              <Paper
                key={alias.idAliasID}
                elevation={isSelected ? 2 : 1}
                onClick={() => onSelectPersonalAlias(alias)}
                sx={{
                  p: 1.5,
                  minWidth: 140,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  ...(isSelected ? selectedPaperSx : { '&:hover': { boxShadow: 4 } }),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {isSelected && <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />}
                  <Typography fontWeight="bold" variant="body2">
                    {alias.chrDisplayName || alias.chrAliasName}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  @{alias.chrAliasName}
                </Typography>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Team Member Profiles
          </Typography>
          {teamMembers.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {teamMembers.map((member) => {
                const isSelected = member.aliasId === selectedAliasId;
                const role = (member.role || 'MEMBER').toUpperCase();
                const roleLabel = role === 'PRIMARY' || role === 'ADMIN' ? 'Owner' : 'Agent';
                return (
                  <Paper
                    key={member.aliasId}
                    elevation={isSelected ? 2 : 1}
                    onClick={() => onSelectTeamMember(member)}
                    sx={{
                      p: 1.5,
                      minWidth: 140,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      ...(isSelected ? selectedPaperSx : { '&:hover': { boxShadow: 4 } }),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isSelected && <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />}
                      <Typography fontWeight="bold" variant="body2">
                        {member.displayName || member.aliasUsername}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      @{member.aliasUsername}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {roleLabel}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                No team member profiles yet.
              </Typography>
            </Paper>
          )}
        </Box>

        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Business Aliases
          </Typography>
          {businessAliases.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {businessAliases.map((alias) => {
                const isSelected = alias.idAliasID === selectedAliasId;
                return (
                  <Paper
                    key={alias.idAliasID}
                    elevation={isSelected ? 2 : 1}
                    onClick={() => onSelectBusinessAlias(alias)}
                    sx={{
                      p: 1.5,
                      minWidth: 140,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                      ...(isSelected ? selectedPaperSx : { '&:hover': { boxShadow: 4 } }),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isSelected && <CheckCircleIcon color="primary" sx={{ fontSize: 16 }} />}
                      <Typography fontWeight="bold" variant="body2">
                        {alias.chrDisplayName || alias.chrAliasName}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      @{alias.chrAliasName}
                    </Typography>
                    {alias.bitIsPrimary && (
                      <Chip size="small" color="primary" label="Primary" sx={{ mt: 0.25, width: 'fit-content' }} />
                    )}
                    {viewerHasBusinessFullAccess && !alias.bitIsPrimary && onSetPrimaryBusinessAlias && (
                      <Button
                        size="small"
                        sx={{ mt: 0.25, width: 'fit-content' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSetPrimaryBusinessAlias(alias);
                        }}
                      >
                        Set as Primary
                      </Button>
                    )}
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                No premium name aliases yet.
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileAliasNavigator;
